import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTheme } from "@mui/material/styles";
import axiosInstance from "../axiosConfig";

const lowStockThreshold = 5;

const groupByCategory = (items) =>
    items.reduce((acc, item) => {
        acc[item.category] = acc[item.category] || [];
        acc[item.category].push(item);
        return acc;
    }, {});

export default function InventoryDashboard() {
    const [inventory, setInventory] = useState([]);
    const [editedStock, setEditedStock] = useState({});
    const [editedMinStock, setEditedMinStock] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [stockFilter, setStockFilter] = useState("all"); // values: 'all', 'in', 'out'

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [openDialog, setOpenDialog] = useState(false);
    const [newItem, setNewItem] = useState({
        name: "",
        category: "",
        stock: 0,
        minstock:0,
        unit: "",
        store: "",
    });
    const [editItem, setEditItem] = useState(null);

    const handleEdit = (item) => {
        setEditItem({ ...item }); // Open modal with item data
    };

    useEffect(() => {
        fetchInventory();
    }, []);

    const fetchInventory = () => {
        axiosInstance.get(`/inventory`).then((response) => setInventory(response.data));
    };

    const filteredInventory = inventory.filter((item) => {
        const matchesSearch =
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.category.toLowerCase().includes(searchTerm.toLowerCase());

        let matchesStock = true;
        if (stockFilter === "in") {
            matchesStock = item.stock > item.minstock;
        } else if (stockFilter === "out") {
            matchesStock = item.stock < item.minstock;
        }

        return matchesSearch && matchesStock;
    });

    const groupedInventory = groupByCategory(filteredInventory);


    const handleStockChange = (id, value) => {
        setEditedStock((prev) => ({
            ...prev,
            [id]: Number(value),
        }));
    };

    const handleMinStockChange = (id, value) => {
        setEditedMinStock((prev) => ({
            ...prev,
            [id]: Number(value),
        }));
    };

    const handleSave = (id) => {
        const updated = inventory.map((item) =>
            item.id === id ? { ...item, stock: editedStock[id] ?? item.stock } : item
        );
        setInventory(updated);
        axiosInstance.put(`/inventory/${id}`, { stock: editedStock[id] }).catch(console.error);
        const updatedEdits = { ...editedStock };
        delete updatedEdits[id];
        setEditedStock(updatedEdits);
    };

    const handleDelete = (id) => {
        axiosInstance.delete(`/inventory/${id}`).then(() => {
            setInventory((prev) => prev.filter((item) => item.id !== id));
        });
    };

    const handleDialogOpen = () => setOpenDialog(true);
    const handleDialogClose = () => {
        setNewItem({ name: "", category: "", stock: 0, unit: "", store: "",minstock:0 });
        setOpenDialog(false);
    };

    const handleNewItemChange = (field, value) => {
        setNewItem((prev) => ({ ...prev, [field]: value }));
    };

    const handleAddNewItem = () => {
        axiosInstance.post("/inventory", newItem).then((res) => {
            setInventory([...inventory, res.data]);
            handleDialogClose();
        });
    };

    const exportToCSV = () => {
        const csvContent = [
            ["ID", "Name", "Category", "Stock", "Unit","MinStock","Store"],
            ...filteredInventory.map((item) => [
                item.id,
                item.name,
                item.category,
                item.stock,
                item.unit,
                item.minstock,
                item.store || "",
            ]),
        ]
            .map((e) => e.join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "filtered_inventory.csv");
        link.click();
    };

    return (
        <Box sx={{ p: 2, pt: 12, maxWidth: 960, margin: "auto", bgcolor: "#fff", minHeight: "100vh" }}>
            <Typography variant="h5" align="center" gutterBottom fontWeight="bold">
                📦 Inventory Dashboard
            </Typography>

            {/* Controls */}
            <Box mb={2} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flexGrow: 1, maxWidth: 140 }}
                />

                <TextField
                    select
                    label="Stock Filter"
                    variant="outlined"
                    size="small"
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{ maxWidth: 140 }}
                >
                    <option value="all">All</option>
                    <option value="in">In Stock</option>
                    <option value="out">Out of Stock</option>
                </TextField>

                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportToCSV}
                >
                    Export CSV
                </Button>

                <Button variant="contained" startIcon={<AddIcon />} onClick={handleDialogOpen}>
                    Add Item
                </Button>
            </Box>

            {isMobile ? (
                Object.entries(groupedInventory).length === 0 ? (
                    <Typography align="center" color="text.secondary">
                        No items found
                    </Typography>
                ) : (
                    Object.entries(groupedInventory).map(([category, items]) => (
                        <Accordion key={category} disableGutters elevation={1} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f7f7f7" }}>
                                <Typography fontWeight="600">{category}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {items.map((item) =>
                                    (
                                    <Box
                                        key={item.id}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        py={0.5}
                                        sx={{
                                            bgcolor: item.stock < lowStockThreshold ? "#ffeaea" : "inherit",
                                            px: 1,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography sx={{ flex: 1, fontSize: "0.75rem", textAlign: "left" }} noWrap>
                                            {item.name}

                                            <Typography sx={{ width: 30, fontSize: "0.75rem" }}>{item.unit}
                                            ( Min :  {item.minstock} )</Typography>
                                        </Typography>

                                        <TextField
                                            type="text"
                                            size="small"
                                            value={editedStock[item.id] ?? item.stock}
                                            onChange={(e) => handleStockChange(item.id, e.target.value)}
                                            inputProps={{ min: 0, style: { textAlign: "right", width: 50 } }}
                                            sx={{ mx: 1, maxWidth: 50 }}
                                        />


                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <img
                                                src="https://img.icons8.com/ios-filled/20/save--v1.png"
                                                alt="Save"
                                                onClick={() => handleSave(item.id)}
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    cursor: item.id in editedStock ? "pointer" : "not-allowed",
                                                    opacity: item.id in editedStock ? 1 : 0.4,
                                                }}
                                                title="Save"
                                            />
                                            <img
                                                src="https://img.icons8.com/ios-glyphs/20/filled-trash.png"
                                                alt="Delete"
                                                onClick={() => handleDelete(item.id)}
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    cursor: "pointer",
                                                }}
                                                title="Delete"
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))
                )
            ) : (
                <>
                    {filteredInventory.length === 0 ? (
                        <Typography align="center" color="text.secondary">
                            No items found
                        </Typography>
                    ) : (
                        <>
                            <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>Category</b></TableCell>
                                            <TableCell><b>Item Name</b></TableCell>
                                            <TableCell><b>Stock</b></TableCell>
                                            <TableCell><b>Min Stock</b></TableCell>
                                            <TableCell><b>Unit</b></TableCell>
                                            <TableCell><b>Store</b></TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredInventory
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((item) => (
                                                <TableRow
                                                    key={item.id}
                                                    hover
                                                    sx={{
                                                        bgcolor: item.stock < lowStockThreshold ? "#ffeaea" : "inherit",
                                                    }}
                                                >
                                                    <TableCell>{item.category}</TableCell>
                                                    <TableCell>{item.name}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={editedStock[item.id] ?? item.stock}
                                                            onChange={(e) => handleStockChange(item.id, e.target.value)}
                                                            inputProps={{ min: 0, style: { textAlign: "right", width: 60 } }}
                                                            sx={{ mx: 1, maxWidth: 60 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{item.minstock}</TableCell>
                                                    <TableCell>{item.unit}</TableCell>
                                                    <TableCell>{item.store}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <img
                                                                src="https://img.icons8.com/ios-filled/20/save--v1.png"
                                                                alt="Save"
                                                                onClick={() => handleSave(item.id)}
                                                                style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    cursor: item.id in editedStock ? "pointer" : "not-allowed",
                                                                    opacity: item.id in editedStock ? 1 : 0.4,
                                                                }}
                                                                title="Save"
                                                            />

                                                            <img
                                                                src="https://img.icons8.com/ios-glyphs/20/filled-trash.png"
                                                                alt="Delete"
                                                                onClick={() => handleDelete(item.id)}
                                                                style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    cursor: "pointer",
                                                                }}
                                                                title="Delete"
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            <TablePagination
                                component="div"
                                count={filteredInventory.length}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[5, 10, 25]}
                            />
                        </>
                    )}
                </>
            )}

            {/* Add Item Dialog */}
            <Dialog open={openDialog} onClose={handleDialogClose} fullWidth>
                <DialogTitle>Add New Inventory Item</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Item Name"
                        fullWidth
                        margin="dense"
                        value={newItem.name}
                        onChange={(e) => handleNewItemChange("name", e.target.value)}
                    />
                    <TextField
                        label="Category"
                        fullWidth
                        margin="dense"
                        value={newItem.category}
                        onChange={(e) => handleNewItemChange("category", e.target.value)}
                    />
                    <TextField
                        label="Stock"
                        type="number"
                        fullWidth
                        margin="dense"
                        value={newItem.stock}
                        onChange={(e) => handleNewItemChange("stock", e.target.value)}
                    />
                    <TextField
                        label="Unit"
                        fullWidth
                        margin="dense"
                        value={newItem.unit}
                        onChange={(e) => handleNewItemChange("unit", e.target.value)}
                    />
                    <TextField
                        label="Store"
                        fullWidth
                        margin="dense"
                        value={newItem.store}
                        onChange={(e) => handleNewItemChange("store", e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Cancel</Button>
                    <Button onClick={handleAddNewItem} variant="contained" startIcon={<AddIcon />}>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
